import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import CtaSection from '../components/CtaSection';
import { fetchCollection, getOptimizedImageUrl } from '../lib/cms';

export default function CasesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeIndustry = searchParams.get('industry') || 'all';

  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const params = { sort: 'order', order: 'asc', limit: 25 };
    if (activeIndustry !== 'all') {
      params.term = `industry:${activeIndustry}`;
    }
    fetchCollection('cases', params).then((res) => {
      setCases(res.items || []);
      setLoading(false);
    });
  }, [activeIndustry]);

  const categories = [
    { id: 'all', label: 'Alle brancher' },
    { id: 'restaurant', label: 'Gastronomi' },
    { id: 'hotel', label: 'Hoteller' },
    { id: 'airbnb', label: 'Airbnb & Ferieboliger' },
    { id: 'cafe', label: 'Café & Bageri' },
    { id: 'cleaning', label: 'Rengøring & Facility' },
    { id: 'retail', label: 'Detail & Livsstil' },
  ];

  return (
    <>
      <SeoHead
        title="Cases & Referencer | SoMe & Content"
        description="Udforsk udvalgte cases inden for hoteller, Airbnb, gastronomi, café, service og detailhandel, der har styrket deres synlighed med fængende shortform content."
      />

      {/* Hero */}
      <section className="pt-40 pb-16 md:pt-48 lg:pt-52 md:pb-24 bg-[#FAF8F5] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl space-y-6">
            <span className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#EAE4DC] text-xs font-semibold uppercase tracking-widest text-[#4A4640]">
              Cases & Resultater
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#161514] leading-[1.1]">
              Udvalgte opgaver &{' '}
              <span className="text-[#876F50]">cases</span>
            </h1>

            <p className="text-lg text-[#5C5852] leading-relaxed">
              Se eksempler på, hvordan professionelt klippet indhold og kontinuerlig udgivelse på sociale medier skaber opmærksomhed og flere kunder.
            </p>
          </div>
        </div>
      </section>

      {/* Filter and Grid */}
      <section className="py-16 md:py-24 bg-[#FAFAF8] border-b border-[#E8E2D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Industry Filter Buttons */}
          <div className="flex flex-wrap items-center gap-2.5 pb-12">
            {categories.map((cat) => {
              const isActive = activeIndustry === cat.id;
              return (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => {
                    if (cat.id === 'all') {
                      searchParams.delete('industry');
                      setSearchParams(searchParams);
                    } else {
                      setSearchParams({ industry: cat.id });
                    }
                  }}
                  className={`px-5 py-2.5 rounded-full text-xs font-semibold transition-all cursor-pointer ${
                    isActive
                      ? 'bg-[#161514] text-[#FAF8F5] shadow-xs'
                      : 'bg-[#FAF8F5] text-[#4A4640] border border-[#E5DFD6] hover:bg-[#EAE4DC]'
                  }`}
                >
                  {cat.label}
                </button>
              );
            })}
          </div>

          {/* Cases Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 animate-pulse">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="aspect-16/10 bg-[#EAE4DC] rounded-3xl" />
              ))}
            </div>
          ) : cases.length === 0 ? (
            <div className="p-12 text-center bg-[#FAF8F5] border border-[#E5DFD6] rounded-3xl space-y-4">
              <p className="text-base text-[#5C5852]">
                Ingen projekter fundet i den valgte kategori i øjeblikket.
              </p>
              <button
                type="button"
                onClick={() => setSearchParams({})}
                className="px-6 py-2.5 rounded-full bg-[#161514] text-white text-xs font-semibold"
              >
                Nulstil filter
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {cases.map((item) => (
                <Link
                  key={item.id}
                  to={`/cases/${item.case_slug}`}
                  className="group block bg-white border border-[#E5DFD6] rounded-3xl overflow-hidden shadow-xs hover:shadow-md transition-shadow"
                >
                  <div className="aspect-16/10 overflow-hidden bg-[#161514] relative">
                    <img
                      src={getOptimizedImageUrl(item.cover_image?.[0], {
                        width: 900,
                        quality: 82,
                        format: 'auto',
                      })}
                      alt={item.title}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-103"
                      loading="lazy"
                    />
                  </div>

                  <div className="p-7 sm:p-9 space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-2xl font-bold text-[#161514] group-hover:text-[#876F50] transition-colors">
                        {item.client_name}
                      </h3>
                      <span className="w-9 h-9 rounded-full bg-[#FAF8F5] border border-[#E5DFD6] text-[#161514] flex items-center justify-center text-sm group-hover:bg-[#161514] group-hover:text-white transition-colors">
                        <i className="fa-regular fa-arrow-right" aria-hidden="true" />
                      </span>
                    </div>

                    <p className="text-sm text-[#5C5852] line-clamp-2 leading-relaxed">
                      {item.challenge}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      <CtaSection
        title="Klar til at løfte din virksomheds synlighed?"
        subtitle="Hver forretning er forskellig, og derfor har vi ingen faste prispakker. Alle aftaler laves individuelt med 0 binding. Udnyt vores kampagne: Betal for 2 mdr, få 1 mdr gratis!"
        primaryButtonText="Kontakt os for en snak"
        primaryButtonLink="/kontakt"
      />
    </>
  );
}
